import { useState, useEffect } from 'react';
import { X, Shield, Smartphone, Copy, Download, CheckCircle, AlertTriangle, Key, Sparkles } from 'lucide-react';
import { use2FA, TwoFactorSetup } from '../hooks/use2FA';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export function TwoFactorSetupModal({ isOpen, onClose, onComplete }: TwoFactorSetupModalProps) {
  const { initialize2FA, enable2FA } = use2FA();
  const [step, setStep] = useState<'setup' | 'verify' | 'backup'>('setup');
  const [setupData, setSetupData] = useState<TwoFactorSetup | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showVerificationInput, setShowVerificationInput] = useState(false);

  // Auto-show verification input after QR code is displayed
  useEffect(() => {
    if (step === 'verify' && setupData) {
      const timer = setTimeout(() => {
        setShowVerificationInput(true);
      }, 5000); // Show input after 5 seconds (increased from 2)
      return () => clearTimeout(timer);
    }
  }, [step, setupData]);

  // Auto-advance when user starts typing valid code
  useEffect(() => {
    if (verificationCode.length === 6 && /^\d{6}$/.test(verificationCode)) {
      // Auto-verify when 6 digits are entered
      const timer = setTimeout(() => {
        handleVerifyAndEnable();
      }, 500); // Small delay for UX
      return () => clearTimeout(timer);
    }
  }, [verificationCode]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const animateStep = (newStep: typeof step) => {
    setIsVisible(false);
    setTimeout(() => {
      setStep(newStep);
      setIsVisible(true);
    }, 150);
  };

  const handleInitialize = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await initialize2FA();
      setSetupData(data);
      animateStep('verify');
    } catch (err) {
      setError('Failed to initialize 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter a verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await enable2FA(verificationCode);
      if (result.success) {
        animateStep('backup');
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch (err) {
      setError('Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    animateStep('setup');
    setSetupData(null);
    setVerificationCode('');
    setError(null);
    setTimeout(() => {
      onComplete();
      onClose();
    }, 300);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backupCodes) return;

    const content = `Payinthi DMS 2FA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nBackup Codes:\n${setupData.backupCodes.join('\n')}\n\nIMPORTANT:\n- Store these codes in a safe place\n- Each code can only be used once\n- Use these codes if you lose access to your authenticator app`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payinthi-dms-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCodeInput = (value: string) => {
    const code = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(code);
    setError(null); // Clear any previous errors
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center p-4 z-[9999] transition-all duration-300">
      
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/20 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className={`bg-white/10 dark:bg-slate-800/10 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-white/20 dark:border-slate-700/30 transform transition-all duration-500 ${isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'} relative`}>
        
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-indigo-500/10" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-400/20 to-transparent rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/20 dark:border-slate-700/30 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 relative z-10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg transform transition-all duration-300 hover:scale-110 hover:rotate-3 group">
              <Shield className="w-5 h-5 text-white transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Setup 2FA</h2>
              <p className="text-sm text-white/60">Secure your account</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 dark:hover:bg-slate-600/60 rounded-xl transition-all duration-300 text-white/60 hover:text-white/90 transform hover:scale-110 group"
          >
            <X className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90" />
          </button>
        </div>

        {/* Content */}
        <div className={`p-6 relative z-10 transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-2xl transform animate-pulse">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
          )}

          {step === 'setup' && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 text-blue-500 mx-auto mb-4 transform transition-all duration-500 hover:scale-110 hover:rotate-3 group">
                  <Smartphone className="w-full h-full transition-transform duration-300 group-hover:scale-110" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                  Enable Two-Factor Authentication
                </h3>
                <p className="text-white/60 text-sm">
                  Add an extra layer of security to your account using an authenticator app.
                </p>
              </div>

              <div className="bg-blue-500/20 border border-blue-500/30 rounded-2xl p-4 transform transition-all duration-300 hover:bg-blue-500/25 group">
                <h4 className="font-medium text-blue-300 mb-2">Before you start:</h4>
                <ul className="text-sm text-blue-200 space-y-1">
                  <li className="flex items-center space-x-2 transform transition-transform duration-300 group-hover:translate-x-1">
                    <span>•</span>
                    <span>Install an authenticator app on your phone</span>
                  </li>
                  <li className="flex items-center space-x-2 transform transition-transform duration-300 group-hover:translate-x-1" style={{ transitionDelay: '100ms' }}>
                    <span>•</span>
                    <span>Have your phone ready to scan a QR code</span>
                  </li>
                  <li className="flex items-center space-x-2 transform transition-transform duration-300 group-hover:translate-x-1" style={{ transitionDelay: '200ms' }}>
                    <span>•</span>
                    <span>Make sure you can access your backup storage</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleInitialize}
                disabled={loading}
                className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 transform hover:scale-105 hover:-translate-y-1 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Shield className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:scale-110" />
                <span className="relative z-10">{loading ? 'Setting up...' : 'Start Setup'}</span>
              </button>
            </div>
          )}

          {step === 'verify' && setupData && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  Scan QR Code
                </h3>
                <p className="text-white/60 text-sm mb-4">
                  Use your authenticator app to scan this QR code
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-2xl shadow-xl transform transition-all duration-500 hover:scale-105 hover:rotate-1 group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-100/20 to-emerald-100/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <img src={setupData.qrCodeUrl} alt="2FA QR Code" className="w-48 h-48 relative z-10 transition-transform duration-300 group-hover:scale-105" />
                  
                  {/* Scanning animation overlay */}
                  <div className="absolute inset-4 border-2 border-green-500/30 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute inset-0 border-2 border-green-400 rounded-xl animate-ping" />
                  </div>

                  {/* Smart detection hint */}
                  {!showVerificationInput && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-sm font-medium">Scan to continue</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Manual Setup - Always visible but smaller */}
              <div className="bg-white/10 rounded-2xl p-3 transform transition-all duration-300 hover:bg-white/15 group">
                <details className="group/details">
                  <summary className="text-sm font-medium text-white/70 cursor-pointer hover:text-white/90 transition-colors duration-200">
                    Can't scan? Enter manually
                  </summary>
                  <div className="mt-2 pt-2 border-t border-white/20">
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 bg-black/20 px-3 py-2 rounded-lg border text-sm font-mono text-white/90 border-white/20 transition-all duration-300 group-hover:border-white/40">
                        {setupData.secret}
                      </code>
                      <button
                        onClick={() => copyToClipboard(setupData.secret)}
                        className="p-2 text-white/60 hover:text-white/90 transition-all duration-300 hover:scale-110 hover:bg-white/10 rounded-lg group"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                      </button>
                    </div>
                  </div>
                </details>
              </div>

              {/* Verification Input - Appears automatically or when needed */}
              <div className={`transform transition-all duration-500 ${showVerificationInput ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95 pointer-events-none'}`}>
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-2 animate-pulse">
                    <Key className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                    Enter your code
                  </h4>
                  <p className="text-white/60 text-sm">
                    Type the 6-digit code from your authenticator
                  </p>
                </div>

                <div className="relative group mb-4">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => handleCodeInput(e.target.value)}
                    placeholder="000000"
                    className="w-full px-6 py-4 border border-white/20 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white/10 text-white text-center text-2xl font-mono placeholder-white/40 group-hover:border-white/40 focus:scale-105 tracking-widest"
                    maxLength={6}
                    autoFocus
                  />
                  
                  {/* Input progress indicator */}
                  <div className="flex justify-center mt-2 space-x-1">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          i < verificationCode.length 
                            ? 'bg-blue-400 scale-100' 
                            : 'bg-white/20 scale-75'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Auto-verify indicator */}
                  {verificationCode.length === 6 && !loading && (
                    <div className="absolute -bottom-8 left-0 right-0 text-center">
                      <span className="text-sm text-green-400 animate-pulse">
                        Auto-verifying...
                      </span>
                    </div>
                  )}

                  {/* Input glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none blur-xl" />
                </div>

                {/* Manual verify button - hidden when auto-verifying */}
                {verificationCode.length < 6 && (
                  <button
                    onClick={handleVerifyAndEnable}
                    disabled={loading || verificationCode.length !== 6}
                    className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-3 rounded-2xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 transform hover:scale-105 hover:-translate-y-1 group relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <CheckCircle className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:scale-110" />
                    <span className="relative z-10">{loading ? 'Verifying...' : 'Verify & Enable'}</span>
                  </button>
                )}
              </div>

              {/* Quick access button to show input early */}
              {!showVerificationInput && (
                <button
                  onClick={() => setShowVerificationInput(true)}
                  className="w-full flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-2xl transition-all duration-300 border border-white/20 hover:border-white/40 transform hover:scale-105 group"
                >
                  <Key className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                  <span>I've scanned the code</span>
                </button>
              )}
            </div>
          )}

          {step === 'backup' && setupData && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 text-green-500 mx-auto mb-4 transform transition-all duration-500 hover:scale-110 hover:rotate-3 group relative">
                  <CheckCircle className="w-full h-full transition-transform duration-300 group-hover:scale-110" />
                  {/* Success animation */}
                  <div className="absolute inset-0 bg-green-400/30 rounded-full animate-ping" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  2FA Enabled Successfully!
                </h3>
                <p className="text-white/60 text-sm">
                  Save these backup codes in a safe place
                </p>
              </div>

              <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-4 transform transition-all duration-300 hover:bg-amber-500/25 group">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 transition-transform duration-300 group-hover:scale-110" />
                  <div>
                    <h4 className="font-medium text-amber-300">Important!</h4>
                    <p className="text-sm text-amber-200 mt-1">
                      These backup codes can be used to access your account if you lose your authenticator device. Each code can only be used once.
                    </p>
                  </div>
                </div>
              </div>

              {/* Backup Codes */}
              <div className="bg-white/10 rounded-2xl p-4 transform transition-all duration-300 hover:bg-white/15 group">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-white/70 flex items-center space-x-2">
                    <Sparkles className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                    <span>Backup Codes</span>
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => copyToClipboard(setupData.backupCodes.join('\n'))}
                      className="flex items-center space-x-1 text-sm text-blue-400 hover:text-blue-300 transition-all duration-300 hover:scale-105"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Copy</span>
                    </button>
                    <button
                      onClick={downloadBackupCodes}
                      className="flex items-center space-x-1 text-sm text-green-400 hover:text-green-300 transition-all duration-300 hover:scale-105"
                    >
                      <Download className="w-3 h-3" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {setupData.backupCodes.map((code, index) => (
                    <div 
                      key={index} 
                      className="bg-black/20 px-3 py-2 rounded-lg border border-white/20 transform transition-all duration-300 hover:scale-105 hover:bg-black/30 hover:border-amber-400/50"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <code className="text-sm font-mono text-white/90">{code}</code>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleComplete}
                className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-1 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CheckCircle className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:scale-110" />
                <span className="relative z-10">Complete Setup</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}